/**
 * Accessibility utilities and constants for tools.dance frontend
 * Provides reusable ARIA labels, accessibility helpers, and constants
 */

// ARIA label constants for common UI elements
export const ARIA_LABELS = {
  // Navigation
  MAIN_NAVIGATION: 'Main navigation',
  USER_MENU: 'User account menu',
  SKIP_TO_CONTENT: 'Skip to main content',
  BREADCRUMB: 'Breadcrumb navigation',

  // Forms
  REQUIRED_FIELD: 'Required field',
  PASSWORD_TOGGLE: 'Toggle password visibility',
  FORM_ERROR: 'Form validation error',
  FORM_SUCCESS: 'Form submission successful',

  // Buttons and Actions
  CLOSE_DIALOG: 'Close dialog',
  OPEN_MENU: 'Open menu',
  CLOSE_MENU: 'Close menu',
  SUBMIT_FORM: 'Submit form',
  CANCEL_ACTION: 'Cancel action',

  // Theme and Settings
  TOGGLE_THEME: 'Toggle between light and dark theme',
  THEME_LIGHT: 'Switch to light theme',
  THEME_DARK: 'Switch to dark theme',

  // Dashboard
  DASHBOARD_WIDGET: 'Dashboard widget',
  STATISTICS_CARD: 'Statistics card',
  ACTIVITY_FEED: 'Recent activity feed',
  QUICK_ACTIONS: 'Quick actions menu',

  // Status and Feedback
  LOADING: 'Loading content',
  ERROR_MESSAGE: 'Error message',
  SUCCESS_MESSAGE: 'Success message',
  WARNING_MESSAGE: 'Warning message',
  INFO_MESSAGE: 'Information message',
} as const;

// ARIA role constants
export const ARIA_ROLES = {
  BANNER: 'banner',
  MAIN: 'main',
  NAVIGATION: 'navigation',
  COMPLEMENTARY: 'complementary',
  CONTENTINFO: 'contentinfo',
  SEARCH: 'search',
  FORM: 'form',
  DIALOG: 'dialog',
  ALERT: 'alert',
  ALERTDIALOG: 'alertdialog',
  STATUS: 'status',
  PROGRESSBAR: 'progressbar',
  TAB: 'tab',
  TABPANEL: 'tabpanel',
  TABLIST: 'tablist',
} as const;

// Live region politeness levels
export const ARIA_LIVE = {
  OFF: 'off',
  POLITE: 'polite',
  ASSERTIVE: 'assertive',
} as const;

// TypeScript interfaces for accessibility props
export interface AccessibilityProps {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-hidden'?: boolean;
  'aria-live'?: keyof typeof ARIA_LIVE;
  'aria-atomic'?: boolean;
  'aria-busy'?: boolean;
  'aria-controls'?: string;
  'aria-current'?: boolean | 'page' | 'step' | 'location' | 'date' | 'time';
  'aria-disabled'?: boolean;
  'aria-invalid'?: boolean | 'grammar' | 'spelling';
  'aria-pressed'?: boolean;
  'aria-required'?: boolean;
  'aria-selected'?: boolean;
  role?: string;
  tabIndex?: number;
}

export interface FormAccessibilityProps extends AccessibilityProps {
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-errormessage'?: string;
}

export interface ButtonAccessibilityProps extends AccessibilityProps {
  'aria-pressed'?: boolean;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
}

// Utility functions for accessibility
export const accessibilityUtils = {
  /**
   * Generates a unique ID for accessibility purposes
   * @param prefix - Optional prefix for the ID
   * @returns Unique ID string
   */
  generateId: (prefix = 'a11y'): string =>
    `${prefix}-${Math.random().toString(36).substr(2, 9)}`,

  /**
   * Creates ARIA attributes for form fields with validation
   * @param fieldName - Name of the form field
   * @param isRequired - Whether the field is required
   * @param hasError - Whether the field has validation errors
   * @param errorId - ID of the error message element
   * @returns Object with ARIA attributes
   */
  getFormFieldProps: (
    fieldName: string,
    isRequired = false,
    hasError = false,
    errorId?: string
  ): FormAccessibilityProps => ({
    'aria-required': isRequired,
    'aria-invalid': hasError,
    'aria-describedby': hasError && errorId ? errorId : undefined,
    'aria-errormessage': hasError && errorId ? errorId : undefined,
  }),

  /**
   * Creates ARIA attributes for buttons with state
   * @param label - Accessible label for the button
   * @param isPressed - Whether the button is in pressed state
   * @param isExpanded - Whether the button controls expanded content
   * @param controlsId - ID of the element controlled by this button
   * @returns Object with ARIA attributes
   */
  getButtonProps: (
    label: string,
    isPressed?: boolean,
    isExpanded?: boolean,
    controlsId?: string
  ): ButtonAccessibilityProps => ({
    'aria-label': label,
    'aria-pressed': isPressed,
    'aria-expanded': isExpanded,
    'aria-controls': controlsId,
  }),

  /**
   * Creates ARIA attributes for live regions
   * @param politeness - Level of politeness for announcements
   * @param atomic - Whether to announce the entire region or just changes
   * @returns Object with ARIA attributes
   */
  getLiveRegionProps: (
    politeness: keyof typeof ARIA_LIVE = 'POLITE',
    atomic = false
  ): AccessibilityProps => ({
    'aria-live': politeness.toLowerCase() as keyof typeof ARIA_LIVE,
    'aria-atomic': atomic,
  }),

  /**
   * Creates skip link for keyboard navigation
   * @param targetId - ID of the target element to skip to
   * @param label - Label for the skip link
   * @returns Skip link props
   */
  getSkipLinkProps: (targetId: string, label = 'Skip to main content') => ({
    href: `#${targetId}`,
    'aria-label': label,
    className: 'skip-link', // CSS class for styling
  }),

  /**
   * Validates color contrast ratio (basic implementation)
   * @param foreground - Foreground color (hex)
   * @param background - Background color (hex)
   * @returns Whether the contrast meets WCAG AA standards
   */
  validateContrast: (_foreground: string, _background: string): boolean =>
    // This is a simplified implementation
    // In a real application, you'd use a proper color contrast library
    // For now, we'll return true and implement proper validation later
    true,
  /**
   * Announces content to screen readers
   * @param message - Message to announce
   * @param priority - Priority level for the announcement
   */
  announceToScreenReader: (
    message: string,
    priority: keyof typeof ARIA_LIVE = 'POLITE'
  ): void => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority.toLowerCase());
    announcement.setAttribute('aria-atomic', 'true');
    announcement.setAttribute('class', 'sr-only');
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove the announcement after a short delay
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  },
};

// Screen reader only CSS class utility
export const SR_ONLY_CLASS = 'sr-only';

// Focus management utilities
export const focusUtils = {
  /**
   * Traps focus within a container element
   * @param container - Container element to trap focus within
   * @returns Function to remove the focus trap
   */
  trapFocus: (container: HTMLElement): (() => void) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    // Focus the first element
    firstElement?.focus();

    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Restores focus to a previously focused element
   * @param element - Element to restore focus to
   */
  restoreFocus: (element: HTMLElement | null): void => {
    if (element && typeof element.focus === 'function') {
      element.focus();
    }
  },

  /**
   * Gets all focusable elements within a container
   * @param container - Container to search within
   * @returns Array of focusable elements
   */
  getFocusableElements: (container: HTMLElement): HTMLElement[] => {
    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors));
  },
};

// Keyboard navigation constants
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  SPACE: ' ',
  TAB: 'Tab',
  ESCAPE: 'Escape',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
  PAGE_UP: 'PageUp',
  PAGE_DOWN: 'PageDown',
} as const;

export default {
  ARIA_LABELS,
  ARIA_ROLES,
  ARIA_LIVE,
  accessibilityUtils,
  focusUtils,
  KEYBOARD_KEYS,
  SR_ONLY_CLASS,
};
